import { camelCase, snakeCase } from 'lodash';
import {
  type CamelCasedPropertiesDeep,
  type SnakeCasedPropertiesDeep,
} from 'type-fest';

import { TO_NOT_TRANSFORM, TO_ALWAYS_TRANSFORM } from './constants';
import { splitKeyIntoSegments, joinSegmentsIntoKey } from '../formData';

type SyntaxModifier = (str: string) => string;

interface ConfigWithData {
  data?: unknown;
  params?: unknown;
}

interface ResponseWithData {
  data: unknown;
}

/**
 * Type that represents a config with transformed params and data keys.
 * Only params and data are transformed, rest of the config remains unchanged.
 */
type ConfigWithTransformedKeys<T extends ConfigWithData> = Omit<
  T,
  'data' | 'params'
> & {
  data?: SnakeCasedPropertiesDeep<T['data']>;
  params?: SnakeCasedPropertiesDeep<T['params']>;
};

/**
 * Type that represents a response with transformed data keys.
 * Only data is transformed, rest of the response (headers, status, etc.) remains unchanged.
 */
type ResponseWithTransformedData<T extends ResponseWithData> = Omit<
  T,
  'data'
> & {
  data: CamelCasedPropertiesDeep<T['data']>;
};

/**
 * Transform config params and data keys to snake_case for API requests.
 * Only transforms keys in params and data, rest of the config is unchanged.
 */
export const configToSnakeCase = <T extends ConfigWithData>(
  config: T,
): ConfigWithTransformedKeys<T> => {
  const { params, data, ...rest } = config;

  return {
    ...rest,
    params: keysSnakeCaseModifier(params),
    data: keysSnakeCaseModifier(data),
  } as ConfigWithTransformedKeys<T>;
};

/**
 * Transform response data keys to camelCase for JavaScript consumption.
 * Only transforms keys in data, rest of the response (headers, status, etc.) is unchanged.
 */
export const responseToCamelCase = <T extends ResponseWithData>(
  response: T,
): ResponseWithTransformedData<T> => {
  const { data, ...rest } = response;

  return {
    ...rest,
    data: keysCamelCaseModifier(data),
  } as ResponseWithTransformedData<T>;
};

/**
 * Rules for determining if a key should skip syntax transformation.
 * These rules check the key name itself (not the path).
 */
const KEY_TRANSFORMATION_RULES = [
  {
    name: 'custom_attributes',
    description: `
      Custom attribute keys use special prefixes and should not be transformed.

      Prefixes:
      - gte_, lte_ : greater than/less than query parameters
      - min__, max__ : min/max query parameters
      - _ : custom attribute field names (e.g., _8f23c2dd_DS)

      Related: https://hivebrite.atlassian.net/browse/PS-11494
    `,
    shouldSkip: (key: string): boolean =>
      /^(gte_|lte_|min__|max__|_).*/.test(key),
  },
  {
    name: 'non_standard_keys',
    description: `
      Non-standard snake_case keys that don't follow typical naming conventions.
      These are legacy keys that must be preserved exactly as-is.
    `,
    shouldSkip: (key: string): boolean => TO_NOT_TRANSFORM.includes(key),
  },
];

/**
 * Keys that should ALWAYS be transformed regardless of other rules.
 */
const FORCE_TRANSFORM_KEYS = TO_ALWAYS_TRANSFORM;

/**
 * Check if a key should skip syntax transformation.
 *
 * @param key - The key to check
 * @returns True if the key should not be transformed
 */
const shouldSkipKeySyntaxModifier = (key: string): boolean => {
  // Force transform takes precedence
  if (FORCE_TRANSFORM_KEYS.includes(key)) {
    return false;
  }

  // Check if any rule matches
  return KEY_TRANSFORMATION_RULES.some((rule) => rule.shouldSkip(key));
};

/**
 * Transform FormData keys while preserving bracket structure.
 *
 * This function handles keys like 'attendees[0][_customField]' by:
 * 1. Splitting the key into segments between brackets
 * 2. Transforming each segment independently (respecting shouldSkip rules)
 * 3. Reassembling with preserved brackets
 *
 * Examples:
 * - 'firstName' → 'first_name'
 * - 'location[country]' → 'location[country]'
 * - 'attendees[0][_8f23c2dd_DS]' → 'attendees[0][_8f23c2dd_DS]' (custom attribute preserved)
 * - 'location[customAttribute]' → 'location[custom_attribute]'
 *
 * @param key - The FormData key to transform
 * @param syntaxModifier - The transformation function (camelCase or snakeCase)
 * @param shouldPreserveKeys - Whether to preserve all keys (from path rules)
 * @returns The transformed key with preserved brackets
 */
const transformFormDataKey = (
  key: string,
  syntaxModifier: SyntaxModifier,
): string => {
  // No brackets? Use standard transformation
  if (!key.includes('[')) {
    return shouldSkipKeySyntaxModifier(key) ? key : syntaxModifier(key);
  }

  const segments = splitKeyIntoSegments(key);
  const parentPath: Array<string> = [];
  segments.forEach((segment, index) => {
    const shouldPreserveKeys = shouldPreserveKeysAtPath(parentPath);
    const shouldSkip = shouldSkipKeySyntaxModifier(segment);
    const keepOriginal = shouldPreserveKeys || shouldSkip;

    segments[index] = keepOriginal ? segment : syntaxModifier(segment);

    parentPath.push(segment);
  });

  return joinSegmentsIntoKey(segments);
};

/**
 * Rules for preserving key case in specific paths.
 * Add new rules here when you need to prevent key transformation in certain contexts.
 */
const PRESERVE_KEYS_RULES = [
  {
    name: 'double_select_options',
    description: `
      Preserve user-defined keys in double_select custom attribute options.

      Problem: Admins can create double_select options with any case (e.g., "A", "B", "c").
      However, lodash's camelCase() transforms all single letters to lowercase: camelCase('A') => 'a'.
      This breaks the DoubleSelect component because it cannot match the stored value "A" with
      the transformed option key "a", resulting in the child select not displaying.

      Solution: Skip key transformation for keys inside json_options.options to preserve exact case.

      Related: https://hivebrite.atlassian.net/browse/EV-1659
    `,
    // Match paths like ["json_options"|"jsonOptions", "options"]
    // Path uses original keys before transformation, so we check both variants:
    // - Response (backend→frontend): ["json_options", "options"]
    // - Request (frontend→backend): ["jsonOptions", "options"]
    matches: (path: Array<string>): boolean => {
      if (path.length < 2) {
        return false;
      }

      const parentKey = path[path.length - 2];
      const currentKey = path[path.length - 1];

      return (
        (parentKey === 'json_options' || parentKey === 'jsonOptions') &&
        currentKey === 'options'
      );
    },
  },
  {
    name: 'poll_votes_results',
    description: `
      Preserve UUID keys in poll votes results.

      Poll votes results are a hash where keys are option IDs.
      These IDs must be preserved exactly as-is to match option.id values.

      Example:
      {
        "019aeae1-6cbc-7a28-a52c-1c8f83905036": { ... },
      }
    `,
    // Match paths like ["votes", "results"]
    matches: (path: Array<string>): boolean => {
      if (path.length < 2) {
        return false;
      }

      const parentKey = path[path.length - 2];
      const currentKey = path[path.length - 1];

      return parentKey === 'votes' && currentKey === 'results';
    },
  },
];

/**
 * Check if the current path matches a pattern where keys should not be transformed.
 * This is used to preserve user-defined keys in specific contexts.
 *
 * @param path - The current path in the object tree
 * @returns True if keys at this path should be preserved
 */
const shouldPreserveKeysAtPath = (path: Array<string>): boolean =>
  PRESERVE_KEYS_RULES.some((rule) => rule.matches(path));

export const keysCamelCaseModifier = <T>(
  object: T,
): CamelCasedPropertiesDeep<T> =>
  keysSyntaxModifier(camelCase, object) as CamelCasedPropertiesDeep<T>;

export const keysSnakeCaseModifier = <T>(
  object: T,
): SnakeCasedPropertiesDeep<T> =>
  keysSyntaxModifier(snakeCase, object) as SnakeCasedPropertiesDeep<T>;

const keysSyntaxModifier = (
  syntaxModifier: SyntaxModifier,
  object: unknown,
): unknown => {
  // Recursive function to pass through each nested object to change keys syntax
  // parentPath tracks the path to detect json_options.options for double_select custom attributes
  const appliedSyntaxToObject = (
    obj: unknown,
    parentPath: Array<string> = [],
  ): unknown => {
    if (Array.isArray(obj)) {
      return obj.map((v) => appliedSyntaxToObject(v, parentPath));
    }
    if (isFormData(obj)) {
      const modifiedFormData = new FormData();
      const formData = obj;

      formData.forEach((value, key) => {
        const transformedKey = transformFormDataKey(key, syntaxModifier);

        modifiedFormData.append(
          transformedKey,
          keysSyntaxModifier(syntaxModifier, value) as File | string,
        );
      });

      return modifiedFormData;
    }
    if (isADefinedObject(obj)) {
      const shouldPreserveKeys = shouldPreserveKeysAtPath(parentPath);

      return Object.keys(obj as Record<string, unknown>).reduce(
        (result, key) => {
          const shouldSkip =
            shouldSkipKeySyntaxModifier(key) || shouldPreserveKeys;
          const transformedKey = shouldSkip ? key : syntaxModifier(key);

          return {
            ...result,
            [transformedKey]: appliedSyntaxToObject(
              (obj as Record<string, unknown>)[key],
              [...parentPath, key], // Use original key for path tracking, not transformed
            ),
          };
        },
        {},
      );
    }

    return obj;
  };

  return appliedSyntaxToObject(object);
};

const isADefinedObject = (object: unknown): object is Record<string, unknown> =>
  object !== null &&
  object !== undefined &&
  (object as Record<string, unknown>)?.constructor === Object;

const isFormData = (object: unknown): object is FormData =>
  (object as Record<string, unknown>)?.constructor === FormData;
